import React, { useState } from "react";
import { Icon } from "@iconify/react/dist/iconify.js";
import EditableInfoRow from "./EditableInfoRow";

const ADDRESS_TYPES = [
    { id: "REGISTERED", name: "Registered" },
    { id: "CORRESPONDENCE", name: "Correspondence" },
    { id: "BILLING", name: "Billing" }
];

const MAX_ADDRESSES = 3;

const AddressSection = ({
    addresses = [],
    isEditing,
    isLoading,
    onEdit,
    onSave,
    onCancel,
    onChange,
    states = [],
    cities = [],
    loadingStates = false,
    loadingCities = false,
    onFetchCities,
    onAddAddress,
    onRemoveAddress,
    onSetPrimary
}) => {
    const [collapsed, setCollapsed] = useState(true);
    const [expandedIndex, setExpandedIndex] = useState(0);

    // Find the index of the primary address (only first one counts)
    const primaryIndex = addresses.findIndex(addr => addr.is_primary === 1);

    // Get used address types
    const usedAddressTypes = addresses.map(addr => addr.addressType || addr.address_type);

    // Get available address types (not yet used)
    const getAvailableAddressTypes = (currentType) => {
        return ADDRESS_TYPES.filter(type =>
            type.id === currentType || !usedAddressTypes.includes(type.id)
        );
    };

    // Check if can add more addresses
    const canAddAddress = addresses.length < MAX_ADDRESSES;

    // Get next available address type for new address
    const getNextAvailableType = () => {
        const availableType = ADDRESS_TYPES.find(type => !usedAddressTypes.includes(type.id));
        return availableType?.id || "CORRESPONDENCE";
    };

    const handleFieldChange = (index, field, value) => {
        onChange(index, field, value);
    };

    const handleSetPrimary = (index) => {
        // Set this address as primary, unset all others
        if (onSetPrimary) {
            onSetPrimary(index);
        }
    };

    // Check if this address is the primary one (only one can be primary)
    const isPrimaryAddress = (index) => {
        if (isEditing) {
            return addresses[index]?.is_primary === 1;
        }
        // In view mode, only the first address with is_primary=1 or index 0 is shown as primary
        return primaryIndex >= 0 ? index === primaryIndex : index === 0;
    };

    const handleStateChange = (index, field, value) => {
        onChange(index, field, value);
        const selectedState = states.find(s => String(s.state_id) === String(value));
        onChange(index, "state", selectedState ? selectedState.name : "");
        onChange(index, "cityId", "");
        onChange(index, "city", "");
        if (onFetchCities) {
            onFetchCities(value);
        }
    };

    const handleCityChange = (index, field, value) => {
        onChange(index, field, value);
        const selectedCity = cities.find(c => String(c.city_id) === String(value));
        onChange(index, "city", selectedCity ? selectedCity.name : "");
    };

    // Check if there are any validation errors
    const hasValidationErrors = addresses.some(address => {
        const pincode = address.pincode || "";
        const hasPincodeError = pincode.length > 0 && pincode.length < 6;
        const missingRequired = !(address.addressLine1 || address.address_line1)?.trim()
            || !(address.stateId || address.state_id)
            || !(address.cityId || address.city_id)
            || !pincode.trim();
        return hasPincodeError || missingRequired;
    });

    return (
        <div className="tw-bg-white tw-rounded-xl tw-shadow-sm tw-border tw-border-slate-100 tw-mb-4">
            {/* Header */}
            <div className="tw-flex tw-items-center tw-justify-between tw-px-5 tw-py-3 tw-border-b tw-border-slate-100 tw-bg-gradient-to-r tw-from-slate-50 tw-to-white">
                <div
                    className="tw-flex tw-items-center tw-gap-2 tw-cursor-pointer tw-select-none"
                    onClick={() => !isEditing && setCollapsed(prev => !prev)}
                >
                    <Icon
                        icon={collapsed && !isEditing ? "solar:alt-arrow-right-bold" : "solar:alt-arrow-down-bold"}
                        className="tw-text-slate-400 tw-text-sm"
                    />
                    <div className="tw-w-8 tw-h-8 tw-rounded-lg tw-bg-amber-100 tw-flex tw-items-center tw-justify-center">
                        <Icon icon="solar:map-point-bold" className="tw-text-amber-600 tw-text-lg" />
                    </div>
                    <h3 className="tw-text-sm tw-font-semibold tw-text-slate-700">Addresses</h3>
                    <span className="tw-text-xs tw-text-slate-400 tw-bg-slate-100 tw-px-2 tw-py-0.5 tw-rounded-full">
                        {addresses.length} {addresses.length === 1 ? 'address' : 'addresses'}
                    </span>
                </div>
                <div className="tw-flex tw-items-center tw-gap-2">
                    {isEditing ? (
                        <>
                            <button
                                onClick={onCancel}
                                disabled={isLoading}
                                className="tw-px-3 tw-py-1.5 tw-text-xs tw-font-medium tw-text-slate-600 tw-bg-slate-100 tw-rounded-lg hover:tw-bg-slate-200 tw-transition-colors disabled:tw-opacity-50"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={onSave}
                                disabled={isLoading || hasValidationErrors}
                                className="tw-px-3 tw-py-1.5 tw-text-xs tw-font-medium tw-text-white tw-bg-amber-500 tw-rounded-lg hover:tw-bg-amber-600 tw-transition-colors disabled:tw-opacity-50 disabled:tw-cursor-not-allowed tw-flex tw-items-center tw-gap-1"
                            >
                                {isLoading ? (
                                    <>
                                        <div className="tw-w-3 tw-h-3 tw-border-2 tw-border-white tw-border-t-transparent tw-rounded-full tw-animate-spin"></div>
                                        Saving...
                                    </>
                                ) : (
                                    <>
                                        <Icon icon="solar:check-circle-bold" className="tw-text-sm" />
                                        Save
                                    </>
                                )}
                            </button>
                        </>
                    ) : (
                        <button
                            onClick={onEdit}
                            className="tw-px-3 tw-py-1.5 tw-text-xs tw-font-medium tw-text-amber-600 tw-bg-amber-50 tw-rounded-lg hover:tw-bg-amber-100 tw-transition-colors tw-flex tw-items-center tw-gap-1"
                        >
                            <Icon icon="solar:pen-bold" className="tw-text-sm" />
                            Edit
                        </button>
                    )}
                </div>
            </div>

            {/* Content */}
            {(!collapsed || isEditing) && <div className="tw-p-5">
                {addresses.map((address, index) => (
                    <div
                        key={address.address_id || index}
                        className={`tw-border tw-rounded-lg tw-mb-3 ${
                            expandedIndex === index ? "tw-border-amber-200" : "tw-border-slate-200"
                        }`}
                    >
                        {/* Address Header */}
                        <div
                            className={`tw-flex tw-items-center tw-justify-between tw-px-4 tw-py-2.5 tw-cursor-pointer tw-transition-colors ${
                                expandedIndex === index
                                    ? "tw-bg-amber-50"
                                    : "tw-bg-slate-50 hover:tw-bg-slate-100"
                            }`}
                            onClick={() => setExpandedIndex(expandedIndex === index ? -1 : index)}
                        >
                            <div className="tw-flex tw-items-center tw-gap-2">
                                <Icon
                                    icon={expandedIndex === index ? "solar:alt-arrow-down-bold" : "solar:alt-arrow-right-bold"}
                                    className="tw-text-slate-400"
                                />
                                <span className="tw-text-sm tw-font-medium tw-text-slate-700">
                                    {address.addressType || address.address_type || "Address"} Address
                                </span>
                                {isPrimaryAddress(index) && (
                                    <span className="tw-text-xs tw-bg-green-100 tw-text-green-700 tw-px-2 tw-py-0.5 tw-rounded-full">
                                        Primary
                                    </span>
                                )}
                            </div>
                            {isEditing && addresses.length > 1 && (
                                <button
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        onRemoveAddress && onRemoveAddress(index);
                                    }}
                                    className="tw-p-1 tw-text-red-500 hover:tw-bg-red-50 tw-rounded"
                                >
                                    <Icon icon="solar:trash-bin-trash-bold" className="tw-text-lg" />
                                </button>
                            )}
                        </div>

                        {/* Address Content */}
                        {expandedIndex === index && (
                            <div className="tw-p-4 tw-bg-white">
                                {/* Primary Checkbox */}
                                {isEditing && (
                                    <div
                                        onClick={() => handleSetPrimary(index)}
                                        className={`tw-mb-4 tw-p-3 tw-rounded-lg tw-border-2 tw-cursor-pointer tw-transition-all tw-flex tw-items-center tw-gap-3 ${
                                            isPrimaryAddress(index)
                                                ? "tw-border-green-400 tw-bg-green-50"
                                                : "tw-border-slate-200 tw-bg-slate-50 hover:tw-border-green-300 hover:tw-bg-green-50/50"
                                        }`}
                                    >
                                        <div className={`tw-w-5 tw-h-5 tw-rounded-full tw-border-2 tw-flex tw-items-center tw-justify-center tw-transition-all ${
                                            isPrimaryAddress(index)
                                                ? "tw-border-green-500 tw-bg-green-500"
                                                : "tw-border-slate-300 tw-bg-white"
                                        }`}>
                                            {isPrimaryAddress(index) && (
                                                <Icon icon="solar:check-read-bold" className="tw-text-white tw-text-xs" />
                                            )}
                                        </div>
                                        <div className="tw-flex tw-flex-col">
                                            <span className={`tw-text-sm tw-font-medium ${
                                                isPrimaryAddress(index) ? "tw-text-green-700" : "tw-text-slate-600"
                                            }`}>
                                                {isPrimaryAddress(index) ? "Primary Address" : "Set as Primary Address"}
                                            </span>
                                            <span className="tw-text-xs tw-text-slate-400">
                                                {isPrimaryAddress(index)
                                                    ? "This address is used as the default"
                                                    : "Click to make this the default address"}
                                            </span>
                                        </div>
                                        {isPrimaryAddress(index) && (
                                            <Icon icon="solar:verified-check-bold" className="tw-text-green-500 tw-text-xl tw-ml-auto" />
                                        )}
                                    </div>
                                )}
                                <div className="row">
                                    {isEditing && (
                                        <EditableInfoRow
                                            label="Address Type"
                                            value={address.addressType || address.address_type}
                                            displayValue={ADDRESS_TYPES.find(t => t.id === (address.addressType || address.address_type))?.name}
                                            icon="solar:tag-bold-duotone"
                                            colSize="col-md-3"
                                            isEditing={isEditing}
                                            fieldKey="addressType"
                                            onChange={(field, value) => handleFieldChange(index, field, value)}
                                            type="select"
                                            options={getAvailableAddressTypes(address.addressType || address.address_type)}
                                            optionLabel="name"
                                            optionValue="id"
                                        />
                                    )}
                                    <EditableInfoRow
                                        label="Address Line 1"
                                        value={address.addressLine1 || address.address_line1}
                                        icon="solar:map-point-bold-duotone"
                                        colSize={isEditing ? "col-md-9" : "col-md-6"}
                                        isEditing={isEditing}
                                        fieldKey="addressLine1"
                                        onChange={(field, value) => handleFieldChange(index, field, value)}
                                        required
                                    />
                                    <EditableInfoRow
                                        label="Address Line 2"
                                        value={address.addressLine2 || address.address_line2}
                                        icon="solar:map-bold-duotone"
                                        colSize="col-md-6"
                                        isEditing={isEditing}
                                        fieldKey="addressLine2"
                                        onChange={(field, value) => handleFieldChange(index, field, value)}
                                    />
                                    <EditableInfoRow
                                        label="Landmark"
                                        value={address.landmark}
                                        icon="solar:point-on-map-bold-duotone"
                                        colSize="col-md-6"
                                        isEditing={isEditing}
                                        fieldKey="landmark"
                                        onChange={(field, value) => handleFieldChange(index, field, value)}
                                    />
                                    <EditableInfoRow
                                        label="State"
                                        value={address.stateId || address.state_id}
                                        displayValue={address.state?.name || address.stateName}
                                        icon="solar:flag-bold-duotone"
                                        colSize="col-md-3"
                                        isEditing={isEditing}
                                        fieldKey="stateId"
                                        onChange={(field, value) => handleStateChange(index, field, value)}
                                        type="select"
                                        options={states}
                                        optionLabel="name"
                                        optionValue="state_id"
                                        placeholder="Search state..."
                                        loading={loadingStates}
                                        required
                                    />
                                    <EditableInfoRow
                                        label="City"
                                        value={address.cityId || address.city_id}
                                        displayValue={address.city?.name || address.cityName}
                                        icon="solar:city-bold-duotone"
                                        colSize="col-md-3"
                                        isEditing={isEditing}
                                        fieldKey="cityId"
                                        onChange={(field, value) => handleCityChange(index, field, value)}
                                        type="select"
                                        options={cities}
                                        optionLabel="name"
                                        optionValue="city_id"
                                        placeholder="Search city..."
                                        disabled={!(address.stateId || address.state_id)}
                                        loading={loadingCities}
                                        required
                                    />
                                    <EditableInfoRow
                                        label="Pin Code"
                                        value={address.pincode}
                                        icon="solar:map-point-bold-duotone"
                                        colSize="col-md-2"
                                        isEditing={isEditing}
                                        fieldKey="pincode"
                                        onChange={(field, value) => handleFieldChange(index, field, value)}
                                        maxLength={6}
                                        minLength={6}
                                        errorMessage="Pin Code must be exactly 6 digits"
                                        required
                                    />
                                </div>
                            </div>
                        )}
                    </div>
                ))}

                {/* Add Address Button */}
                {isEditing && canAddAddress && (
                    <button
                        onClick={() => onAddAddress(getNextAvailableType())}
                        className="tw-w-full tw-py-2.5 tw-border-2 tw-border-dashed tw-border-slate-300 tw-rounded-lg tw-text-slate-500 hover:tw-border-amber-400 hover:tw-text-amber-600 tw-transition-colors tw-flex tw-items-center tw-justify-center tw-gap-2"
                    >
                        <Icon icon="solar:add-circle-bold" className="tw-text-lg" />
                        Add New Address ({MAX_ADDRESSES - addresses.length} remaining)
                    </button>
                )}
            </div>}
        </div>
    );
};

export default AddressSection;
